<template>
  <div class="landing-page-v2 free-weeks-2024" :class="[$config.platform, { extend: promotion.extend }]">
    <Hero
      :stats="stats" :assets="assets.hero" :benefits="benefits"
      :cols="{ left: 'col-7', right: 'col-5' }"
    >
      <template slot="title">
        <h1 class="main">
          <span class="highlight blue">ALL {{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span>
          <span class="mobile-block">COURSES</span>
          <span class="block">
            <component :is="$config.platform === 'ds' ? 'Free' : 'FreeFa'" />
            <span class="mobile-block">for 21 Days</span>
          </span>
        </h1>
      </template>
      <Countdown slot="countdown" :end-date="countdownEndDate" classes="bordered" />
      <template slot="footer-text">
        <span class="desktop-only">
          <IconCreditCardCircle />
          <span>No credit card required</span>
        </span>
      </template>
      <template slot="content">
        <span class="mobile-only no-card-required">
          <IconCreditCardCircle />
          <span>No credit card required</span>
        </span>
        <div class="signup-wrapper">
          <SignupForm
            heading="p"
            title="Create Your Free Account"
            button-text="Get Unlimited Access"
            button-class="btn-primary"
            type="signup"
            unique-id="first"
            class="signup-form form-desktop"
          />
        </div>
      </template>
    </Hero>
    <section class="technologies dark">
      <p class="technologies-title">
        Learn with instructors from:
      </p>
      <div class="container">
        <div v-for="(tech) in companies" :key="tech.icon" class="single-technology">
          <component
            :is="iconLogo(tech.icon)"
            :class="tech.icon"
            :title="tech.text"
          />
          <span v-if="tech.text" class="text">
            {{ tech.text }}
          </span>
        </div>
      </div>
    </section>
    <section class="section courses-extended">
      <div class="head container">
        <h2 class="section-title">
          Obtain Must-Have <span class="highlight blue">{{ $config.platform === 'ds' ? 'Data and AI' : 'Finance' }}</span> Skills for <span class="highlight yellow">FREE</span>
        </h2>
        <h3 class="muted">
          What’s your ideal job?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in cGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight blue thin">{{ cGroup.description.title }}<span class="mobile-only">,</span></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses
        :key="courseGroupIdx" :courses="cGroupCourses" :offset="-2"
        heading="h3"
      />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_courses.click`"
          @click="subscribe"
        >
          <span>
            Get Unlimited Access
          </span>
        </button>
      </div>
    </section>
    <section class="section promo-video">
      <Video :video-js-loaded="videoJsLoaded" :video-id="$config.platform === 'ds' ? '6363863777112' : '6363863494112'">
        <div slot="title" class="section-title">
          <template v-if="$config.platform === 'ds'">
            <strong class="highlight blue">Upskill</strong> in Data and AI with No Strings Attached
          </template>
          <template v-else>
            <strong class="highlight blue">Upskill</strong> in Finance with No Strings Attached
          </template>
        </div>
      </Video>
      <StatsIcons
        class="container"
        :stats="stats" colored
        :order="['students', 'courses', 'projects']"
      />
    </section>
    <!-- <div class="section stats">
      <StatsIcons
        class="container"
        :stats="stats" colored auto-width
        :order="['students', 'courses', 'projects']"
      />
    </div> -->
    <section class="section contest">
      <h2 class="section-title">
        Learn <span class="highlight blue">21 Days</span> <span class="highlight yellow">FREE</span> and Earn Rewards
      </h2>
      <h3 class="muted">
        Join the <strong>365 {{ $config.platform === 'ds' ? 'Data' : 'Finance' }} Learning Challenge</strong><span v-if="$config.platform === 'ds'"> Today for a Chance to Win</span>
      </h3>
      <div class="container">
        <div class="steps row">
          <div class="step col col-4">
            <div class="step-number">
              1
            </div>
            <p class="step-title">
              CREATE AN ACCOUNT
            </p>
            <p class="step-text muted">
              Sign up for a <span class="highlight blue">free account</span> if you haven’t yet.
            </p>
          </div>
          <div class="step col col-4">
            <div class="step-number">
              2
            </div>
            <p class="step-title">
              LEARN & EARN POINTS
            </p>
            <p class="step-text muted">
              Collect <span class="highlight blue">{{ $config.platform === 'ds' ? 'challenge' : 'study' }} points (XPs)</span> by completing courses, projects, and exams for free.
            </p>
          </div>
          <div class="step col col-4">
            <div class="step-number">
              3
            </div>
            <p class="step-title">
              SHARE & WIN
            </p>
            <p class="step-text muted">
              Share your <span class="highlight blue">certificates on LinkedIn</span> with the hashtag <strong>#{{ $config.platform === 'ds' ? '365Data' : 'Finance' }}LearningChallenge</strong> and tag us <strong>@{{ $config.platform === 'ds' ? '365DataScience' : '365financialanalyst' }}.</strong>
            </p>
          </div>
        </div>
        <div class="prizes">
          <p class="title">
            WINNERS GET:
          </p>
          <p class="description">
            <span class="highlight yellow">Free subscriptions</span> to our expert-led {{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }} TRAINING.
          </p>
          <div class="numbers row">
            <div class="number col col-4">
              <component :is="$config.platform === 'ds' ? 'IconPrizeFirst' : 'IconFAPrizeFirst'" />
              <ul>
                <li><IconCheckAuto />Lifetime Access</li>
                <li><IconCheckAuto />Winner Certificate</li>
                <li><IconCheckAuto />1:1 Career Consultation with 365’s CEO</li>
              </ul>
            </div>
            <div class="number col col-4">
              <component :is="$config.platform === 'ds' ? 'IconPrizeSecond' : 'IconFAPrizeSecond'" />
              <ul>
                <li><IconCheckAuto />Lifetime Access</li>
                <li><IconCheckAuto />{{ $config.platform === 'ds' ? 'Portfolio Feedback' : 'Financial Model Feedback' }}</li>
              </ul>
            </div>
            <div class="number col col-4">
              <component :is="$config.platform === 'ds' ? 'IconPrizeThird' : 'IconFAPrizeThird'" />
              <ul>
                <li><IconCheckAuto />One-Year Аccess</li>
                <li><IconCheckAuto />Resume Feedback</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="footer-description muted">
          After Nov. 21, we’ll contact the top 3 learners to award prizes!
        </div>
      </div>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_prizes.click`"
          @click="subscribe"
        >
          <span>{{ $config.platform === 'ds' ? 'Start Now' : 'Join Now' }}</span>
        </button>
      </div>
    </section>
    <section class="section free">
      <div class="container">
        <h2 class="section-title">
          What’s <span class="highlight blue">FREE</span> for 21 Days?
        </h2>
        <div v-if="$config.platform === 'ds'" class="section-description muted">
          With online courses, practical exercises, exams, and real-world projects, you'll build essential skills and <strong>earn certifications</strong> that set you apart in the job market. At 365 Data Science, we understand you value flexibility and practicality in your learning. That's why we provide comprehensive training programs that teach you essential skills through real-life case studies—all at your own pace.
          <p>Our content and certificates are <span class="highlight yellow">FREE through Nov.21.</span></p>
        </div>
        <div v-else class="section-description muted">
          With online courses, practical exercises, exams, and real-world projects, you'll build essential skills and <strong>earn certifications</strong> to help you stand out in the competitive job market. 365 Financial Analyst empowers today’s busy learners to excel in their finance careers with comprehensive, cutting-edge training programs.
          <p>Our content and certificates are <span class="highlight yellow">FREE through Nov.21.</span></p>
        </div>
        <client-only>
          <div class="cards row">
            <div
              v-for="(card, idx) in benefitCards" :key="idx"
              slot="slide"
              class="card col col-4 sm-6"
              :class="`id-${idx}`"
            >
              <div class="image-wrapper">
                <img :src="require(`../assets/images/campaigns/${$config.platform}-${card.image}.webp`)" class="card-image">
              </div>
              <h4 class="card-title" v-html="card.title" />
              <span class="card-desc muted" v-html="card.text" />
            </div>
          </div>
        </client-only>
      </div>
    </section>
    <section class="section tracks">
      <ProgramCards
        :title="modulesData.title" title-class="section-title" col="col-4"
        inline no-padding-top show-career-tracks
        no-icons :modules-extend="modules[$config.platform]"
      />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_program-cards.click`"
          @click="subscribe"
        >
          <span>Learn for Free</span>
        </button>
      </div>
    </section>
    <section v-if="$config.platform === 'ds'" class="section graduations">
      <Videos :videos="videos" :video-js-loaded="videoJsLoaded">
        <template slot="title">
          <p class="section-title">
            Join a Vibrant Community of <span class="highlight blue">3M+ Students</span>
          </p>
        </template>
      </Videos>
    </section>
    <section v-else class="section long-reviews-holder">
      <Reviews
        :title="reviewsTitle" title-class="title-reset title-highlight"
        class="long-reviews" dark unique-class="second"
      />
    </section>
    <LimitedOffer
      :stats="stats" :assets="assets.footer" :benefits="footerBenefits"
      :info="footerInfo" :col="{ left: 'col-6', right: 'col-5 offset-1' }"
      class="section"
    >
      <template slot="title">
        <span class="mobile-block">Get Your</span>
        <component :is="$config.platform === 'ds' ? 'Free' : 'FreeFa'" class="mobile-block" />
        <span class="block"><span class="highlight blue">{{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span> <span class="mobile-block">{{ $config.platform === 'ds' ? 'Courses' : 'Training' }} Today</span></span>
      </template>
      <div slot="content" class="signup-wrapper">
        <SignupForm
          title="Create Your Free Account"
          button-text="Get Unlimited Access"
          button-class="btn-primary"
          type="signup"
          unique-id="second"
          class="signup-form form-desktop"
        />
      </div>
    </LimitedOffer>
    <client-only>
      <BottomBanner
        v-if="$store.state.layout === 'desktop'"
        :key="1 + countdownEndDate.getTime()"
        :options="bottomBannerOptions"
        :discount="promotion.discount"
        :closed="bannerClosed"
        :style="{ 'background-image': `url(${require(`../assets/images/campaigns/${$config.platform}-fw2024-bg-bottom-banner.webp`)})` }"
        @subscribe="subscribe"
        @close="closeBanner"
      >
        <template slot="heading">
          <div>
            <span class="highlight blue">ALL {{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span>
            <span class="block">COURSES</span>
          </div>
          <component :is="$config.platform === 'ds' ? 'Free' : 'FreeFa'" />
        </template>
        <CounterWithDiscount
          slot="countdown"
          :end-date="countdownEndDate" banner
          discount-text="Current Discount"
          counter-class="filled"
        />
      </BottomBanner>
    </client-only>
  </div>
</template>

<script>
import { CAMPAIGN_PROMOTIONS } from '@itbpbg/lms-components/src/utils/campaigns';
import { getPromotion, dateInRightInterval, loadHotJar } from '@itbpbg/lms-components/src/utils/helper';
import global from '@itbpbg/lms-components/src/mixins/global';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import CounterWithDiscount from '@itbpbg/lms-components/src/CounterWithDiscount';
import Countdown from '@itbpbg/lms-components/src/Countdown';
import StarsRatingCampaign from '@itbpbg/lms-components/src/assets/svg/campaign-stars-rating.svg?vue-component';
import Free from '@itbpbg/lms-components/src/assets/svg/ds-fw2024-free.svg?vue-component';
import FreeFa from '@itbpbg/lms-components/src/assets/svg/fa-fw2024-free.svg?vue-component';
import IconCheckAuto from '@itbpbg/lms-components/src/assets/svg/check-campaign-auto.svg?vue-component';
import Hero from '../components/Campaigns/Hero';
import Courses from '../components/Campaigns/Courses';
import Video from '../components/Campaigns/Video';
import StatsIcons from '../components/Campaigns/StatsIcons';
import SignupForm from '../components/SignupForm';
import ProgramCards from '../components/Landing/ProgramCards';
import Reviews from '../components/Landing/Reviews';
import LimitedOffer from '../components/Campaigns/LimitedOffer';
import BottomBanner from '../components/Landing/BottomBanner';
import IconTrustpilot from '../assets/svg/icon-trustpilot.svg?vue-component';
import IconCreditCardCircle from '../assets/svg/icon-credit-card-circle.svg?vue-component';
import IconPrizeFirst from '../assets/svg/icon-prize-first.svg?vue-component';
import IconPrizeSecond from '../assets/svg/icon-prize-second.svg?vue-component';
import IconPrizeThird from '../assets/svg/icon-prize-third.svg?vue-component';
import IconFAPrizeFirst from '../assets/svg/icon-prize-fa-first.svg?vue-component';
import IconFAPrizeSecond from '../assets/svg/icon-prize-fa-second.svg?vue-component';
import IconFAPrizeThird from '../assets/svg/icon-prize-fa-third.svg?vue-component';

export default {
  components: {
    Hero,
    Companies,
    SliderInfinite,
    Courses,
    Video,
    StatsIcons,
    IconTrustpilot,
    StarsRatingCampaign,
    Free,
    FreeFa,
    Videos,
    CounterWithDiscount,
    Countdown,
    SignupForm,
    ProgramCards,
    Reviews,
    LimitedOffer,
    BottomBanner,
    IconCreditCardCircle,
    IconPrizeFirst,
    IconPrizeSecond,
    IconPrizeThird,
    IconFAPrizeFirst,
    IconFAPrizeSecond,
    IconFAPrizeThird,
    IconCheckAuto,
  },
  mixins: [global],
  async asyncData({
    $axios,
    $config,
    route,
    error,
  }) {
    const {
      page, courses, stats, projects, coursesGroups,
    } = await $axios.$get('/pages/campaign/free-weeks-2024?prices=1&courses=1&coursesExtended=1')
      .catch(() => error({ statusCode: 404, message: 'Page not found' }));

    if (!page) {
      return error({ statusCode: 404, message: 'Page not found' });
    }

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      courses, stats, page, projects, coursesGroups,
    };
  },
  data() {
    return {
      assets: {
        hero: {
          stamp: true,
          background: `${this.$config.platform}-fw2024-bg-hero.webp`,
          backgroundMobile: `${this.$config.platform}-fw2024-bg-hero-mobile.webp`,
        },
        footer: {
          // background: `${this.$config.platform}-fw2024-bg-footer.webp`,
          // backgroundMobile: `${this.$config.platform}-fw2024-bg-footer-mobile.webp`,
        },
        backgroundsAsImages: false,
      },
      videoJsLoaded: false,
      videoAlreadyPlayed: false,
      bannerClosed: false,
      countdownEndDate: this.saleEndDate(),
      promotion: {},
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      },
      instructorsTitle: 'Learn Data Science with <span class="highlight blue">Instructors</span> from<br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>',
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      benefitCards: [
        {
          title: this.$config.platform === 'ds' ? 'Certificates' : 'Finance Certificates',
          text: (this.$config.platform === 'ds' ? 'Demonstrate your data and AI expertise by obtaining certifications that validate your skills.' : 'Demonstrate your finance expertise by earning finance certificates as a <strong>tangible proof</strong> of your skills.'),
          image: 'fw2024-certificates',
        },
        {
          title: 'Personalized Learning Plan',
          text: 'Build the ideal learning journey for your desired job and <strong>strengthen your abilities</strong> with a personalized skill plan.',
          image: 'fw2024-clp',
        },
        {
          title: 'Career-Focused Training',
          text: (this.$config.platform === 'ds' ? 'Embark on the data and <strong>AI career path of your dreams<strong> with a collection of courses to obtain role-specific skills.' : 'Embark on the finance <strong>career path of your dreams</strong> with a collection of courses to obtain role-specific skills.'),
          image: 'fw2024-tracks',
        },
        {
          title: 'Practice Tests',
          text: (this.$config.platform === 'ds' ? 'Test your newfound data and AI skills with our practical exams. Each <strong>course and career-focused training</strong> offers a verifiable certificate.' : 'Test your newfound finance skills with our practical exams. Each course and <strong>career-focused training</strong> offers a verifiable certificate.'),
          image: 'fw2024-exams',
        },
        {
          title: this.$config.platform === 'ds' ? 'Data Projects' : 'Finance Projects',
          text: (this.$config.platform === 'ds' ? 'Boost your portfolio with <strong>real-world data and ML projects</strong>. Learn by doing with business cases you\'ll encounter on the job.' : 'Boost your resume with <strong>real-world finance projects</strong> based on actionable use cases that provide valuable business insights.'),
          image: 'fw2024-projects',
        },
        {
          title: 'Interactive Exercises',
          text: (this.$config.platform === 'ds' ? 'Apply your knowledge with coding tasks, fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.' : 'Apply your knowledge with fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.'),
          image: `fw2024-exercises${this.$config.platform === 'ds' ? '' : '-new'}`,
        },
      ],
      heroAnimation: {
        strings: [
          'data scientist',
          'data analyst',
          'business analyst',
          'ML engineer',
          'AI engineer',
        ],
        index: 0,
        animate: false,
      },
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      technologies: [
        { icon: 'python', text: 'Python' },
        { icon: 'git', text: 'Git and Github' },
        { icon: 'tableau', text: 'Tableau' },
        { icon: 'r' },
        { icon: 'excel', text: 'Excel' },
        { icon: 'jupyter', text: 'Jupyter' },
        { icon: 'sql' },
        { icon: 'chatgpt', text: 'ChatGPT' },
        { icon: 'powerbi', text: 'Power BI' },
      ],
      companies: [
        ...(this.$config.platform === 'ds' ? [
          { icon: 'meta-new' },
          { icon: 'netflix' },
          { icon: 'google-new' },
          { icon: 'spotify' },
          { icon: 'ikea' },
          { icon: 'coca-cola-auto' },
          { icon: 'bbc' },
          { icon: 'pwc-new' },
        ] : [
          { icon: 'hsbc-new' },
          { icon: 'morgan-stanley-new' },
          { icon: 'goldman-sachs-new' },
          { icon: 'pwc-new' },
          { icon: 'coca-cola-auto' },
          { icon: 'ikea-new' },
        ]),
      ],
      companiesSmall: [
        { icon: 'oracle' },
        { icon: 'amazon' },
        { icon: 'walmart' },
        { icon: 'alphabet' },
        { icon: 'meta' },
      ],
      promoCardOptions: {
        labelText: 'Exclusive Offer',
        discountedPriceCharge: '/year',
        billedText: 'Billed annually',
        countdown: false,
        buttonText: 'Save 57% Now',
        subscribeClass: 'subscribe-annual',
        promoPlanType: 'annual',
        version: '_v1',
      },
      instructors: [
        {
          name: 'Davis Balaba',
          slug: 'davis-balaba',
        },
        {
          name: 'Danielle Thé',
          slug: 'danielle-the',
        },
        {
          name: 'Anastasia Kuznetsova',
          slug: 'anastasia-kuznetsova',
        },
        {
          name: 'Bernard Marr',
          slug: 'bernard-marr',
        },
        {
          name: 'Shashank Kalanithi',
          slug: 'shashank-kalanithi-1',
        },
        {
          name: 'Jeff Lee',
          slug: 'jeff-li-1',
        },
        {
          name: 'Lauren Newbould',
          slug: 'lauren-newbould-1',
        },
        {
          name: 'Olivier Maugain',
          slug: 'olivier-maugain',
        },
        {
          name: 'Randy Rosseel',
          slug: 'randy-rosseel',
        },
        {
          name: 'Tina Huang',
          slug: 'tina-huang',
        },
        {
          name: 'Ken Jee',
          slug: 'ken-jee',
        },
      ],
      courseGroupIdx: 0,
      modulesData: {
        title: (this.$config.platform === 'ds' ? 'Embark on an <strong class="highlight blue">EXPERT-LED</strong> PATH to Career Success' : 'Immerse Yourself Into <span class="highlight blue">FREE</span> Career-Focused Training'),
        subtitle: null,
      },
      modules: {
        ds: {
          0: { subtitle: 'Develop the top-level data science skills needed to become the most sought-after professional that drives value in any organization.' },
          1: { subtitle: 'Gain the skills for a data analyst career. Solve issues through an analytical lens and empower businesses to make informed decisions.' },
          2: { subtitle: 'Build the necessary skills for a business analyst job. Help organizations make data-driven decisions and create long-lasting impact.' },
        },
        fa: {
          0: { subtitle: 'Learn everything you need to build a successful financial analysis career. Acquire robust accounting, financial planning, analysis, and reporting skills.' },
          1: { subtitle: 'Prepare for this demanding career by learning financial modeling, investment banking, and corporate finance.' },
          2: { subtitle: 'Gain the skills to research and assess potential investment opportunities and become a sought-after investment analyst or associate.' },
        },
      },
      reviewsTitle: `Join a Vibrant Community of <strong class="highlight blue">${this.$c.text('students.count')}+ Students</strong>`,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    const brightcoveSrc = [
      { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
      { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...this.page, metaImage: this.page.ogImage || `og-campaign-${this.$config.platform}-free-weeks-2024.webp` }),

      script: [
        ...brightcoveSrc,
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  computed: {
    cGroups() {
      const mapped = [
        'AI Engineer',
        'Machine Learning Engineer',
        'Data Analyst',
        'Data Scientist',
        'Business Intelligence Analyst',
        'Data Engineer',
        'Tableau Developer',
        'Power BI Developer',
        'Business Analyst',
      ];

      if (!this.coursesGroups) {
        return {};
      }

      if (this.$config.platform === 'ds') {
        return mapped.map((t) => this.coursesGroups.find((g) => g.name === t) || {});
      }

      return this.coursesGroups;
    },
    cGroup() {
      return this.cGroups[this.courseGroupIdx] || { description: {}, courses: [] };
    },
    cGroupCourses() {
      return this.cGroup.courses.map((id) => {
        if (typeof id === 'object') {
          return id;
        }

        return this.courses.find((c) => c.id === id) || {};
      }).filter((v) => Object.keys(v).length);
    },
    benefits() {
      if (this.$config.platform === 'fa') {
        return [
          `${this.stats.rounded.courses}+ expert-led <strong>courses</strong> on financial modeling, FP&A, accounting, portfolio management, valuation, investment banking, corporate finance, and more`,
          '<strong>Certificates</strong> of achievement',
          'Real-world finance <strong>projects</strong>',
          '<strong>Career-focused</strong> training',
          '<strong>Rewards</strong> for the top 3 learners',
        ];
      }

      return [
        `${this.stats.rounded.courses}+ expert-led <strong>courses</strong> on Python, SQL, data analysis, visualization, ML, AI, and more`,
        '<strong>Certificates</strong> of achievement',
        'Real-world data <strong>projects</strong>',
        '<strong>Career-focused</strong> training',
        '<strong>Rewards</strong> for the top 3 learners',
      ];
    },
    footerInfo() {
      const info = {
        description: 'Make the most of our 21-day free initiative with unlimited access to features tailored to boost your career success.',
      };

      if (this.$config.platform === 'fa') {
        info.description = 'Make the most of our 21-day free initiative with unlimited access to courses and exercises tailored to boost your career.';
      }

      return info;
    },
    footerBenefits() {
      return [
        { name: 'Expert-led courses' },
        { name: 'Self-paced online lessons' },
        { name: 'Interactive exercises' },
        { name: 'Real-world data projects' },
        { name: 'Certificates of achievement' },
        { name: 'Career-focused training' },
        { name: 'Resume builder' },
        { name: 'Community forum' },
      ];
    },
    signupBtnText() {
      if (this.$config.platform === 'fa') {
        return 'Get Started';
      }

      return this.$c.text('button.get-started');
    },
    salary() {
      if (!this.cGroup.description.earn) {
        return '';
      }

      const [salary, year] = this.cGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
    limitedOfferOptions() {
      const price = this.page && this.page.prices.price ? this.page.prices.price : 119;
      const baseAnnualPrice = this.page && this.page.prices.basePriceComparison ? this.page.prices.basePriceComparison : 432;

      return {
        preTitleSlot: true,
        htmltitle: `with a 365 Annual Plan at <span class="highlight blue">${this.promotion.discount}% OFF</span>`,
        promoPlanType: 'annual',
        comparisonPlanType: 'monthly',
        comparisonPlanBilledText: 'Billed Monthly',
        promoPlanBilledText: 'Billed Annually',
        regularPrice: this.$options.filters.formatCurrency(this.page.prices.basePrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPrice: this.$options.filters.formatCurrency(price, this.prices.currency, { minimumFractionDigits: 0 }),
        billedComparisonPrice: this.$options.filters.formatCurrency(baseAnnualPrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPriceCharge: '/year',
        billedComparisonCharge: '/month',
        promoPlanSubscribeClass: 'subscribe-annual',
        comparisonSubscribeClass: 'subscribe-monthly',
        promoPlanButtonText: 'Get Annual Plan',
        buttonClass: 'submit',
        comparisonPlanButtonText: 'Sign up Now',
        vat: 'VAT may be applied',
        badges: this.$config.platform === 'ds',
        badgesNeutral: true,
      };
    },
    bottomBannerOptions() {
      return {
        counterWithDiscount: true,
        subscribeClass: 'submit campaign-1',
        buttonText: 'Start Now',
        className: this.promotion.final ? 'final-promo' : '',
        futureProof: true,
      };
    },
    footerFeatures() {
      const data = [
        { name: `${this.stats.rounded.courses} data and AI courses` },
        { name: 'Industry-recognized certificates ' },
        { name: 'Interactive exercises' },
        { name: 'Resume builder' },
        { name: 'Real-world data projects' },
        { name: 'Portfolio feedback' },
        { name: 'Downloadable materials' },
        { name: 'Personalized support' },
        { name: 'Role-specific certification programs' },
        { name: 'Gamified learning experience' },
        { name: 'Hands-on exams' },
        { name: 'Community forum ' },
      ];

      if (this.$store.state.layout === 'mobile') {
        return [
          ...data.filter((v, i) => i % 2 === 0),
          ...data.filter((v, i) => i % 2 === 1),
        ];
      }

      return data;
    },
    prices() {
      return this.page.prices;
    },
    priceSplitted() {
      return (+this.prices.price || 0).toString().split('.');
    },
  },
  created() {
    this.countdownEndDate = this.saleEndDate();
  },
  async mounted() {
    loadHotJar();

    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    // Hero animation
    setInterval(() => {
      this.heroAnimation.animate = true;
      setTimeout(() => {
        this.heroAnimation.index = (this.heroAnimation.index + 1) % this.heroAnimation.strings.length;
      }, 300);

      setTimeout(() => {
        this.heroAnimation.animate = false;
      }, 600);
    }, 1200);

    this.$nextTick(() => this.toggleBanner());
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    this.handleScroll();

    let stop = false;
    const interval = setInterval(() => {
      if (stop) {
        clearInterval(interval);
      }

      const nowDate = new Date();
      if (dateInRightInterval(nowDate)) {
        this.countdownEndDate = this.saleEndDate();
      }

      if (nowDate > new Date(this.promotion.endDateFinal)) {
        stop = true;
        window.location.href = `${this.$config.siteUrl}/offer-expired/`;
      }
    }, 1000);

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
  },
  methods: {
    handleScroll() {
      if (process.client) {
        const salary = this.$el && this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }

        const video = this.$el.querySelector('.video-container');
        if (video) {
          const videoBounds = video.getBoundingClientRect();
          if (videoBounds.top < window.innerHeight * 0.7 && videoBounds.top > window.innerHeight * -1 && this.$store.state.layout !== 'mobile' && !this.videoAlreadyPlayed) {
            this.$root.$emit('video-play', this.$config.platform === 'ds' ? '6363863777112' : '6363863494112');
            this.videoAlreadyPlayed = true;
          }
        }

        if (this.$store.state.layout !== 'mobile' && !this.bannerClosed) {
          this.$nextTick(() => this.toggleBanner());
        }
      }
    },
    iconLogo(icon) {
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/companies/logo-${icon}.svg?vue-component`) : '';
    },
    icon(icon) {
      const name = icon === 'sql' ? icon : `tech-responsive-${icon}`;
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/${name}.svg?vue-component`) : '';
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2];
            width += letter.offsetWidth;
          }
          this.salaryAnimation.width = width;
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      if (this.promotion.isFree) {
        this.scrollToLastSection();

        return false;
      }

      const plan = this.$store.getters['subscriptions/annual'] || {};
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'annual', coupon: this.promotion.coupon },
      });
    },
    scrollToLastSection() {
      const limitedOffer = document.querySelector('.limited-offer-holder .signup-wrapper');
      const y = limitedOffer.getBoundingClientRect().top + window.scrollY - 50;
      window.scrollTo({ top: y, behavior: 'smooth' });
    },
    toggleBanner() {
      const bottomBanner = document.querySelector('.bottom-banner');
      const hero = document.querySelector('.technologies');
      const footer = document.querySelector('.limited-offer-holder');
      let hideFromTop = false;
      let hideFromBottom = false;

      if (bottomBanner) {
        if (hero) {
          hideFromTop = hero.getBoundingClientRect().top - 100 > 0;
        }

        if (footer) {
          hideFromBottom = footer.getBoundingClientRect().top - 400 < 0;
        }

        if (hideFromTop || hideFromBottom) {
          bottomBanner.classList.remove('fixed');
        } else {
          bottomBanner.classList.add('fixed');
        }
      }
    },
    closeBanner() {
      this.bannerClosed = true;
    },
    saleEndDate() {
      const campaignData = CAMPAIGN_PROMOTIONS['free-weeks-2024'];
      const promoOptions = getPromotion(campaignData.promos, this.promotion);
      this.promotion = {
        buttonText: 'Start Now',
        ...promoOptions.nextPromotion,
        ...campaignData.templates,
      };
      if (promoOptions.refresh) {
        setTimeout(() => {
          window.$nuxt.refresh();
        }, 40000);
      }

      return new Date(this.promotion.endDate);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes heroAnimation {
  0% {
    transform: rotateX(0deg);
  }

  50% {
    transform: rotateX(90deg);
  }

  100% {
    transform: rotateX(0deg);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only,
  ::v-deep .mobile-only,
  ::v-deep .laptop-only {
    display: none !important;
  }
}

.block {
  display: block;
}

.highlight,
::v-deep .highlight {
  font-weight: 700;

  &.campaign-1,
  &.blue {
    color: var(--c-fw2024-1);

    &.special {
      text-shadow: 4px 4px 0 var(--main-dark);
    }
  }

  &.campaign-2,
  &.yellow {
    color: var(--c-fw2024-2);

    &.special {
      text-shadow: -1px -1px 0 var(--main-dark), 1px -1px 0 var(--main-dark), -1px 1px 0 var(--main-dark), 1px 1px 0 var(--main-dark), 4px 4px 0 var(--c-lda-2);
    }
  }

  &.white {
    font-weight: 600;
    color: var(--main-light);
  }

  &.thin {
    font-weight: inherit;
  }
}

::v-deep {
  .course-card.dark {
    .course-card-body {
      .course-info {
        .course-name {
          font-size: 18px;
          font-weight: 500;
        }

        .info {
          font-size: 16px;
          font-weight: 400;
        }
      }
    }

    .course-card-container {
      background-color: var(--main-dark-light-1);
    }
  }
}

.extend-label {
  margin-top: 30px;
  display: inline-block;
  font-size: 24px;
  font-weight: 700;
  padding: 12px;
  line-height: 18px;
  background-color: var(--c-lda-3);
  transform: rotate(-5deg);
}

.btn-primary,
::v-deep .btn-primary {
  color: var(--main-dark);
  border: 2px solid var(--main-dark);
  background-color: var(--c-fw2024-2);
  font-weight: 600;
  font-size: 21px;
  border-radius: 0;
  margin: 0 auto;
  display: block;
  height: 58px;
  line-height: 18px;
  max-width: 350px;
  width: 100%;
  padding-left: 5px;
  padding-right: 5px;
  position: relative;
  z-index: 1;

  // span {
  //   position: relative;
  //   z-index: 2;
  // }

  &:not(.submit) {
    border: none;
    background-color: transparent;
    transition: all 150ms ease-in-out;
    transform: translate(-15px, -19px);
    margin-top: 60px;

    &:after {
      content: '';
      position: absolute;
      display: block;
      background-color: var(--c-fw2024-2);
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
    }

    &:before {
      content: '';
      position: absolute;
      display: block;
      background-image: url('../assets/images/campaigns/ds-fw2024-bg-btn.webp');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      z-index: -2;
      top: 0;
      right: -15px;
      bottom: -19px;
      left: 0;
      transition: all 150ms ease-in-out;
    }

    span:before {
      content: '';
      position: absolute;
      display: block;
      z-index: -1;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transition: all 150ms ease-in-out;
    }
  }

  &.submit {
    transition: all 50ms linear;
    border-color: var(--main-dark);
    background-color: var(--c-fw2024-2);
  }

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-fw2024-2);

      &:not(.submit) {
        border: none;
        border-color: var(--c-fw2024-2);
        background-color: transparent;
        transform: translate(0, 0);

        &:before {
          left: 15px;
          top: 19px;
          transform: translate(-15px, -19px);
        }

        span:before {
          top: -19px;
          left: -15px;
        }
      }

      &.submit {
        background-color: var(--c-fw2024-2);
        border-color: var(--main-dark);
        outline: 2px solid var(--main-dark) !important;
      }
    }
  }
}

.fa {
  .btn-primary,
  ::v-deep .btn-primary{
    &:before {
      background-image: url('../assets/images/campaigns/fa-fw2024-bg-btn.webp');
    }
  }
}

::v-deep .limited-offer .btn-primary.submit,
::v-deep .bottom-banner .btn-primary.submit {
  background-color: var(--c-mw-2);

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-mw-2);
    }
  }
}

.landing-page-v2 {
  background-color: var(--main-dark);
  color: var(--main-light);
  min-height: 100vh;

  :not(.hero) .btn-primary {
    width: 350px;
  }

  h1 {
    font-size: 48px;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 40px;

    .code-block img {
      height: 35px;
      display: block;
      margin-bottom: var(--spacing-8);
    }
  }

  .section-title,
  ::v-deep .section-title {
    font-size: 40px;
    margin: 0;

    // .highlight {
    //   font-weight: inherit;
    // }

    .smaller {
      display: block;
      margin-top: 12px;
      font-size: 32px;
    }
  }

  .muted {
    color: var(--main-light-shade-4);
  }

  .subtitle {
    font-size: 18px;
    font-weight: 500;
  }

  .animation {
    font-family: "Source Code Pro", monospace;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
    display: inline-block;
    font-size: 18px;
    line-height: 18px;
    width: 180px;
    height: 23px;
    color: var(--main-dark);
    background-color: #F0F0F2;
    border: 1px solid #ABAFB5;
    text-align: center;
    transform-style: preserve-3d;

    &.animate {
      animation: heroAnimation 0.6s ease-in-out;
    }
  }

  .static-salary-container {
    width: 188px;
    display: inline-block;
  }

  .animation-salary {
    vertical-align: middle;
    display: inline-block;
    width: 188px;

    .holder {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 0;

      &.animation {
        text-align: left;
        line-height: 23px;
      }

      &.active {
        transition: width 0.5s;
        width: var(--width, 188px);
      }
    }

    ::v-deep {
      .letter {
        font-weight: normal;
        opacity: 0;
        transition: all 0.1s linear;

        &.show {
          opacity: 1;
        }

        &:first-child {
          padding-left: 4px;
        }

        &:last-child {
          padding-right: 4px;
        }

        &.bold {
          font-weight: 700;
        }
      }
    }
  }

  .section {
    background-size: cover;
    background-position: bottom;
    padding-top: 80px;
    padding-bottom: 80px;

    &.instructors {
      background-color: var(--main-dark-light-1);

      ::v-deep .infinite-head .h4 {
        text-align: center;
      }
    }
  }

  &.extend {
    .highlight.yellow.special {
      text-shadow: 4px 4px 0 var(--main-dark);
    }

    .btn-primary,
    ::v-deep .btn-primary {
      &.submit:not(:hover) {
        box-shadow: 6px 6px 0 0 var(--c-lda-3);
      }
    }
  }

  .form-wrap {
    box-shadow: 0 0 40px 0 #25F4EE;
    margin-left: auto;
    border-radius: 0;
    padding: 44px;

    ::v-deep {
      .header .h4 {
        text-align: center;
        color: var(--main-dark);
      }
    }
  }

  .hero {
    min-height: 800px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url('../assets/images/campaigns/ds-fw2024-bg-hero.webp');
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
    padding-top: 0;

    ::v-deep .trusted-companies {
      margin-top: var(--spacing-36);
      padding-top: var(--spacing-36);
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 32px;
        column-gap: 66px;

        .logo {
          svg {
            height: 24px;
            color: var(--main-light-shade-3);

            g[fill] {
              fill: var(--main-light-shade-3);
            }

            &.pwc {
              height: 28px;
            }
          }
        }
      }
    }

    .content .title .main {
      font-size: 32px;

      .highlight {
        font-size: 56px;
        font-weight: 800;
      }

      > .mobile-block {
        font-weight: 800;
      }

      .block {
        .mobile-block {
          vertical-align: top;
          display: inline-block;
          margin-top: 20px;
        }
      }

      svg {
        height: 106px;
      }
    }

    ::v-deep .hero-benefits {
      font-size: 18px;
      line-height: 1.4;

      &.desktop-only {
        margin: 40px 0;
        max-width: 80%;
      }
    }

    ::v-deep .countdown {
      color: var(--main-light);
      margin-bottom: 20px;

      .box {
        border-radius: 0;
        background-color: var(--main-dark);
        border-color: var(--c-fw2024-1);
      }
    }

    ::v-deep .footer-text {
      > span {
        display: flex;
        align-items: center;
      }

      svg {
        [fill] {
          fill: var(--main-light);
        }

        [stroke] {
          stroke: var(--main-light);
        }
      }

      span {
        font-size: 18px;
        margin-left: 16px;
      }
    }

    .holder {
      .footer-text {
        display: none;
      }
    }
  }

  &.fa {
    .hero {
      background-image: url('../assets/images/campaigns/fa-fw2024-bg-hero.webp');
    }

    .form-wrap {
      box-shadow: 0 0 40px 0 #68A9FE;
    }
  }

  .technologies {
    padding-top: 32px;
    padding-bottom: 32px;

    .technologies-title {
      font-size: 18px;
      font-weight: 500;
      text-align: center;
      margin: 0 0 28px;
    }

    .container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .single-technology {
        display: flex;
        align-items: center;
        color: var(--main-light-shade-3);

        svg {
          height: 24px;
          margin-right: 10px;

          // [fill] {
          //   fill: var(--main-light-shade-3);
          // }

          // [stroke] {
          //   stroke: var(--main-light-shade-3);
          // }
        }
      }
    }
  }

  &.fa .technologies {
    .container .single-technology svg {
      height: 32px;
    }
  }

  .instructors {
    ::v-deep {
      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;
        border: 4px solid var(--main-dark);
        border-radius: 0;

        &:has(
          [href*="balaba"],
          [href*="kuznetsova"],
          [href*="kalanithi"],
          [href*="newbould"],
          [href*="rosseel"],
          [href*="ken-jee"],
        ) {
          box-shadow: 6px 6px 0 0 #25F4EE;
        }

        img {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .instructors-footer {
    border-top: 1px solid var(--main-dark-light-3);
    margin-top: 30px;
    padding-top: 32px;

    ::v-deep .trusted-companies {
      margin-top: 0;
      padding-top: 0;
      border-top: none;

      .title {
        color: var(--main-light);
      }

      .logos {
        .logo {
          svg {
            height: 30px;

            &,
            g[fill] {
              color: var(--main-light-shade-4);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-4);
            }
          }
        }
      }
    }

    .trustpilot-review {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-bottom: 40px;

      svg {
        height: 40px;
        margin-right: 26px;

        ~ svg {
          height: 38px;
          margin-right: 0;
        }
      }

      .rating {
        font-size: 28px;
        font-weight: 500;
        color: var(--main-light-shade-4);
        margin-right: 20px;
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  .stats {
    background-color: var(--main-dark-light-1);

    ::v-deep .stats-icons {
      margin-top: 0;

      > div {
        align-items: center;

        .description {
          font-size: 21px;
        }
      }

      svg {
        width: 90px;
        height: 90px;
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--accent);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;
      }
    }

    .btn-holder,
    .btn-primary {
      margin-top: 0;
    }
  }

  .promo-video {
    .video {
      padding-top: 0;
      padding-bottom: 0;

      ::v-deep .video-js {
        border-radius: 0;
        border: none;
      }
    }

    ::v-deep .stats-icons .description {
      white-space: nowrap;
    }
  }

  .contest {
    .section-title {
      text-align: center;
    }

    > .muted {
      text-align: center;
      font-weight: 400;
    }

    .container {
      background-color: var(--main-dark-light-1);
      padding: 44px;
      margin-top: 60px;
      text-align: center;
      font-size: 18px;

      .steps {
        margin-bottom: 30px;

        .step + .step {
          border-left: 1px solid var(--main-dark-light-4);
        }
      }

      .step-number {
        width: 44px;
        height: 44px;
        line-height: 44px;
        display: inline-block;
        background-color: var(--c-fw2024-1);
        color: var(--main-dark);
        text-align: center;
        border-radius: 50%;
        font-size: 24px;
      }

      .step-title {
        font-size: 24px;
        font-weight: 500;
      }

      .step-text {
        line-height: 1.5;
      }

      .prizes {
        padding: 32px;
        background-color: var(--main-dark);

        .title {
          font-size: 32px;
          font-weight: 500;
          margin: 0;
        }

        .description {
          margin: 0;
        }

        .numbers {
          margin-top: 24px;

          .number ul {
            list-style: none;
            padding-left: 0;

            li {
              font-weight: 400;
              margin-bottom: 6px;

              svg {
                color: var(--c-fw2024-1);
                margin-right: 12px;
                vertical-align: middle;
              }
            }
          }
        }
      }
    }

    .footer-description {
      margin-top: 30px;
    }
  }

  &.fa .contest {
    .container .steps .step-number {
      background-color: var(--accent);
      color: var(--main-light);
    }
  }

  .free {
    .section-title {
      text-align: center
    }

    .section-description {
      text-align: center;
      font-size: 21px;
      font-weight: 400;
      line-height: 1.4;
    }

    .cards {
      row-gap: 30px;

      .card {
        .card-image {
          display: block;
          max-width: 100%;
          margin: auto;
        }

        .card-title {
          margin-top: 20px;
        }
      }
    }
  }

  ::v-deep .infinite-carousel {
    .controls {
      margin-top: 0;
    }

    .carousel {
      padding-top: 10px;
      margin-top: -10px;
      padding-bottom: 10px;
      margin-bottom: -10px;

      .card {
        margin: 0 var(--spacing-30) 0 0;
        width: 370px;
        vertical-align: top;

        .course-card-container {
          min-height: 422px;
        }

        &:not(.course-card) {
          flex-direction: column;
          align-items: flex-start;
          height: auto;
          border-radius: 0;

          .image-wrapper img {
            display: block;
            max-width: 100%;
            max-height: 100%;
            border-radius: 7px;
          }

          .card-title {
            margin-top: var(--spacing-20);
            font-size: 32px;
            color: var(--main-light);
          }

          .card-desc {
            color: var(--main-dark-shade-4);
            font-size: 21px;
            line-height: 140%;
          }
        }

        &.course-card {
          .course-card-image {
            height: 220px;
          }

          .course-card-body {
            padding: var(--spacing-20) var(--spacing-24) 20px;

            .course-name {
              margin-bottom: var(--spacing-8);
            }

            .course-rating {
              color: var(--main-light);
            }

            .info {
              color: var(--main-light);
              margin: 0;

              .dot {
                background: var(--main-light);
              }
            }
          }
        }
      }
    }
  }

  .master {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

    .muted {
      font-size: 21px;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .program-cards.inline ::v-deep {
    .modules .module {
      border-radius: 0;

      &:not(.expand) {
        max-height: 620px;
      }

      .module-info .course > div .course-info .name {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: normal;
      }
    }
  }

  .long-reviews-holder {
    ::v-deep {
      .title {
        text-align: center;
        margin-right: 0;
      }

      .reviews-trustpilot.dark .see-more:after {
        background: linear-gradient(2deg, #011633 2.01%, rgba(1, 22, 51, 0.77) 35.57%, rgba(1, 22, 51, 0.00) 86.79%);
      }
    }
  }

  .graduations {
    background-size: cover;
    background-position: bottom;

    ::v-deep {
      .slider-container.landing .slider-header {
        margin-bottom: 32px;
      }

      .title-highlight {
        font-size: 32px;

        svg {
          height: 38px;
        }
      }

      .graduates-thoughts .videos .single-video .video-image {
        border-radius: 0;
      }
    }

    .heading img {
      height: 44px;
      display: inline-block;
      vertical-align: bottom;
    }
  }

  .dream {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
    text-align: center;

    .title-description {
      font-size: 32px;
      font-weight: 500;
      margin: 0;
    }

    .row {
      margin-top: 60px;
      margin-bottom: 18px;
    }

    .col {
      svg {
        color: var(--accent);
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: var(--accent);
      }

      .description {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }

  ::v-deep .graduates-thoughts {
    h2 {
      max-width: unset;
    }
  }

  ::v-deep .limited-offer-holder {
    position: relative;
    background-image: url('../assets/images/campaigns/ds-fw2024-bg-footer.webp');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center top;

    .title {
      font-size: 32px;
      font-weight: 500;

      .mobile-block:has(+ svg) {
        display: inline-block;
        vertical-align: 23px;
      }

      svg {
        height: 94px;
      }

      .highlight {
        font-size: 56px;
        font-weight: 800;
      }
    }

    .limited-offer {
      position: relative;
      z-index: 2;
      color: var(--main-light);

      h2 {
        margin-bottom: var(--spacing-40);

        .highlight {
          font-weight: 900;
        }
      }

      .subtitle {
        color: var(--main-light);
        font-size: 32px;
        line-height: 1.1;
        margin-bottom: 12px;
      }

      .heading {
        font-size: 40px;

        img {
          display: block;
          margin: 0 auto;
        }

        .text-2 {
          font-size: 44px;
          font-weight: 800;
        }

        .text-3 {
          font-size: 40px;
        }
      }

      .price {
        margin-top: var(--spacing-20);
        margin-bottom: var(--spacing-30);

        .regular-price {
          align-self: flex-start;
          margin-top: 10px;
          font-weight: 500;
          color: #E6E7E9;

          &:before {
            background-color: #E6E7E9;
          }
        }

        .discounted-price {
          line-height: 70px;
          font-weight: 700;
          font-size: 60px;

          .body-1 {
            font-weight: 400;
          }
        }
      }

      .plan-features {
        margin-bottom: var(--spacing-50);

        .feature {
          position: relative;
          line-height: 150%;
          margin-bottom: 8px;

          svg {
            [fill] {
              fill: var(--c-mw-1);
            }

            [stroke] {
              stroke: var(--c-mw-1);
            }
          }
        }
      }

      hr {
        display: none;
      }

      .switch-holder {
        span:not(.unactive) {
          color: var(--main-light);
        }

        .switch {
          width: 45.69px;
          height: 22px;

          .slider:before {
            width: 16px;
            height: 16px;
            top: 3px;
            left: 3px;
          }

          // input:not(:checked) + .slider {
          //   background-color: var(--c-tyf-1);
          // }

          input:checked + .slider {
            background-color: var(--c-tyf-1);

            &:before {
              transform: translateX(23px);
            }
          }
        }
      }

      .btn-primary {
        margin-top: 0;
      }

      .badges {
        margin-top: 40px;
      }

      .vat-note {
        margin-top: var(--spacing-24);
        color: var(--main-light);
        font-weight: 400;
        font-size: 18px;
      }
    }

    .limited-offer-background {
      position: absolute;
      top: 0;
      left: 50%;
      bottom: 0;
      width: 100%;
      height: calc(100% + 50px);
      object-fit: cover;
      transform: translateX(-50%);
    }
  }

  &.fa {
    ::v-deep .limited-offer-holder {
      background-image: url('../assets/images/campaigns/fa-fw2024-bg-footer.webp');
    }
  }

  ::v-deep .bottom-banner {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 110px;
    padding: 0;
    border-top: 3px solid var(--extreme-yellow);
    border-bottom: 3px solid var(--extreme-yellow);

    .container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .campaign-heading {
      display: flex;
      align-items: center;
      font-size: 28px;
      font-weight: 500;
      text-align: right;
      line-height: 0.9;

      div {
        margin-top: 8px;
      }

      .highlight {
        font-size: 40px;
        font-weight: 800;
      }

      .block {
        margin-top: 6px;
      }

      svg {
        height: 70px;
        margin-left: 12px;
      }
    }

    .counter.filled .square {
      border-radius: 0;
      border: 2px solid var(--c-lda-2);
      width: 74px;
      height: 73px;

      .number {
        font-size: 28px;
        line-height: 32px;
      }
    }

    .btn-primary {
      width: 220px;
      height: 58px;
      margin-left: 0;
      margin-right: 0;
      background: var(--extreme-yellow);
      color: var(--main-dark);
      line-height: 20px;
      border: 0;

      &:hover {
        background-color: var(--extreme-yellow);
      }

      &:active {
        background-color: var(--extreme-yellow);
      }
    }

    .icon-close {
      top: 19px;
      right: 14px;

      path[fill] {
        fill: var(--main-dark-light-4);
      }
    }
  }

  @include bp($bp-laptop) {
    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    .btn-primary,
    ::v-deep .btn-primary {
      &:not(.submit) {
        max-width: 310px;

        &:before {
          background-image: url('../assets/images/campaigns/ds-fw2024-bg-btn-laptop.webp');
        }
      }
    }

    &.fa {
      .btn-primary,
      ::v-deep .btn-primary {
        &:before {
          background-image: url('../assets/images/campaigns/fa-fw2024-bg-btn-laptop.webp');
        }
      }
    }

    .extend-label {
      margin-top: 10px;
      font-size: 21px;
      line-height: 12px;
      margin-bottom: 10px;
    }

    .hero {
      .content .title .main {
        font-size: 28px;

        .highlight {
          font-size: 48px;
        }

        svg {
          height: 87px;
        }
      }

      h1 {
        .block {
          font-size: 28px;
        }

        .highlight {
          font-size: 50px;
        }
      }
    }

    ::v-deep .hero-benefits .benefit {
      font-size: 18px;
    }

    .instructors {
      ::v-deep {
        .infinite-carousel .carousel .inner > * {
          width: 178px;
        }
      }
    }

    .instructors-footer ::v-deep {
      .trusted-companies {
        .logos {
          .logo svg {
            height: 22px;
          }
        }
      }
    }

    ::v-deep {
      .infinite-carousel .carousel .card {
        width: 310px;
      }
    }

    .courses-extended {
      .head {
        ul li {
          font-size: 14px;
        }

        .description {
          font-size: 21px;
        }
      }
    }

    .stats-icons {
      max-width: 820px;
    }

    .stats ::v-deep .stats-icons {
      > div {
        width: auto;
      }

      svg {
        width: 60px;
        height: 60px;
      }

      > div .description {
        font-size: 18px;
      }
    }

    .dream {
      .title-description {
        font-size: 28px;
      }

      .col {
        .title {
          font-size: 24px;
        }
      }
    }

    .landing-footer {
      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }
    }

    ::v-deep .limited-offer-holder {
      background-image: url('../assets/images/campaigns/ds-fw2024-bg-footer-laptop.webp');

      .limited-offer {
        .limited-offer-container {
          width: 700px;
          max-width: 700px;
        }

        .subtitle {
          font-size: 28px;
        }

        .heading {
          &,
          .text-2,
          .text-3 {
            font-size: 32px;
          }

          img {
            height: 60px;
          }
        }

        .subtitle .title-inline {
          width: 240px;
        }

        .price {
          margin-bottom: 46px;

          .discounted-price {
            font-size: 54px;
          }
        }
      }

      .limited-offer-background {
        bottom: -160px;
      }

      .title {
        font-size: 28px;

        svg {
          height: 88px;
        }

        .highlight {
          font-size: 48px;
        }
      }
    }

    &.fa ::v-deep .limited-offer-holder {
      background-image: url('../assets/images/campaigns/fa-fw2024-bg-footer-laptop.webp');
    }

    ::v-deep .bottom-banner {
      .campaign-heading {
        font-size: 28px;
        line-height: 0.7;

        .block {
          display: block;
        }

        .highlight {
          font-size: 40px;
        }

        svg {
          height: 60px;
        }
      }

      .btn-primary {
        width: 180px;
        font-size: 18px;
      }
    }

    &.extend {
      ::v-deep .bottom-banner {
        .campaign-heading {
          .extend-label {
            font-size: 14px;
          }

          .highlight {
            font-size: 36px;

            &.blue {
              font-size: 48px;

              .smaller {
                font-size: 32px;
              }
            }
          }
        }

        .counter {
          .square:not(:last-of-type) {
            margin-right: 8px;
          }
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .mobile-block {
      display: block !important;
    }

    .btn-primary,
    ::v-deep .btn-primary {
      &:not(.submit) {
        max-width: 310px;

        &:before {
          background-image: url('../assets/images/campaigns/ds-fw2024-bg-btn-mobile.webp');
        }
      }
    }

    &.fa {
      .btn-primary,
      ::v-deep .btn-primary {
        &:before {
          background-image: url('../assets/images/campaigns/fa-fw2024-bg-btn-mobile.webp');
        }
      }
    }

    .desktop-only,
    ::v-deep .desktop-only {
      display: none !important;
    }

    .extend-label {
      margin: 0 auto 20px;
      font-size: 21px;
      line-height: 12px;
    }

    .btn-holder {
      margin-top: 0;
    }

    .btn-primary.submit,
    ::v-deep .btn-primary.submit {
      font-size: 18px;
      line-height: 14px;
    }

    .section {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: contain;
      background-position: bottom;
      background-repeat: no-repeat;
    }

    :not(.hero) .btn-primary {
      width: 100%;
      max-width: 327px;
    }

    .subtitle {
      font-size: 20px;
    }

    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    ::v-deep .signup-wrapper {
      width: calc(100% + 48px);
      background-color: var(--main-light);
      margin-left: -24px;
      margin-right: -24px;

      .form-wrap {
        margin: 0 auto;
        box-shadow: none;
        padding-top: 60px;
        padding-bottom: 60px;

        .header .h4 {
          font-size: 24px;
        }
      }
    }

    .hero {
      background-image: url('../assets/images/campaigns/ds-fw2024-bg-hero-mobile.webp');
      background-size: auto;
      background-position: center;
      padding-top: 108px;
      padding-bottom: 60px;

      .content .title .main {
        text-align: center;
        font-size: 28px;

        .highlight {
          font-size: 46px;
        }

        svg {
          height: 87px;
        }

        .block {
          display: block;

          .mobile-block {
            margin-top: 0;
          }
        }
      }

      .no-card-required svg {
        [fill] {
          fill: var(--main-light);
        }

        [stroke] {
          stroke: var(--main-light);
        }
      }

      ::v-deep .trusted-companies {
        margin-top: 40px;
        padding-top: 40px;
        margin-bottom: 60px;

        .logos {
          column-gap: 40px;

          .logo svg {
            height: 20px;
          }
        }
      }

      ::v-deep .holder {
        padding: 0;
        margin-top: 0;

        .no-card-required {
          display: flex;
          align-items: center;
          margin-top: 0;
          margin-bottom: 40px;

          svg {
            margin-right: 20px;
          }
        }

        .card {
          padding: 24px;
          margin-right: auto;
          margin-bottom: 0;

          .stamp {
            top: 24px;
            right: 24px;

            img {
              width: 80px;
            }
          }

          .card-text {
            font-size: 12px;
          }

          .old-price {
            font-size: 24px;
          }

          .discounted-price {
            font-size: 59px;
          }

          .text {
            font-size: 18px;
          }
        }
      }

      .mobile-only {
        margin-top: 40px;

        .subtitle {
          font-weight: 400;
        }
      }

      .signup-wrapper {
        width: calc(100% + 48px);
      }
    }

    &.fa .hero {
      background-image: url('../assets/images/campaigns/fa-fw2024-bg-hero-mobile.webp');
    }

    .instructors ::v-deep .infinite-carousel .carousel .inner > * {
      width: 327px;
    }

    .technologies .container {
      padding-top: 36px;
      padding-bottom: 36px;
      flex-wrap: wrap;
      gap: 24px 40px;
      justify-content: space-evenly;
    }

    .instructors-footer {
      padding-left: 0;
      padding-right: 0;
      margin-left: 24px;
      margin-right: 24px;
      width: auto;

      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }

          .logos {
            .logo svg {
              height: 18px;
              margin-right: 0;
            }
          }
        }

        .trustpilot-review {
          margin-top: 0;
          justify-content: space-between;
          max-width: 375px;

          > svg {
            height: 26px;
            margin-right: auto;
            flex: 0 0 auto;

            ~ svg {
              height: 26px;
            }
          }

          .rating {
            font-size: 20px;
            margin-right: auto;
          }
        }
      }

      .btn-primary {
        margin-top: 32px;
      }
    }

    ::v-deep .hero-benefits .benefit {
      font-size: 18px;
    }

    .courses-extended {
      .head {
        .highlight.yellow {
          display: block;
        }

        ul {
          gap: 12px;
          justify-content: flex-start;
        }

        .description {
          margin-top: 32px;

          .highlight.blue {
            display: block;
          }
        }
      }

      .courses {
        padding-top: 32px;
        padding-bottom: 0;
      }

      .btn-holder {
        margin-top: 60px;
      }

      ::v-deep .infinite-carousel .carousel .card {
        width: 327px;
      }
    }

    ::v-deep .program-cards {
      padding-top: 0;
      padding-bottom: 0;

      &.inline .modules .module:not(.show) {
        padding: 0;
      }
    }

    .contest {
      padding-left: 24px;
      padding-right: 24px;

      .container {
        padding-left: 24px;
        padding-right: 24px;

        .prizes {
          padding-left: 24px;
          padding-right: 24px;
        }

        .steps .step + .step {
          border-left: none;
        }
      }
    }

    .stats ::v-deep .stats-icons {
      svg {
        width: 50px;
        height: 50px;
      }
      > div .description {
        font-size: 18px;
        white-space: nowrap;
      }
    }

    .master {
      text-align: center;
    }

    .graduations {
      ::v-deep {
        .col {
          padding-left: 26px;
          padding-right: 26px;
        }
      }
    }

    .dream {
      h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }
    }

    ::v-deep .limited-offer-holder {
      padding-top: 60px;
      padding-bottom: 0;
      background-image: url('../assets/images/campaigns/ds-fw2024-bg-footer-mobile.webp');
      background-size: auto;
      background-position: center top;

      .limited-offer {
        .container {
          h2 {
            margin: 0 auto var(--spacing-40);
          }
        }

        .plan-features {
          text-align: left;
          display: block;
          width: 100%;

          .feature {
            font-size: initial;
          }
        }

        .price {
          align-items: flex-start;

          .regular-price {
            font-size: 24px;
          }

          .discounted-price {
            font-size: 52px;
          }
        }

        .subtitle {
          font-size: 28px;
          line-height: 110%;
          display: block;
          text-align: center;

          .title-inline {
            display: block;
            width: 258px;
            margin: 10px 0;
          }
        }
      }

      .title {
        font-size: 28px;

        svg {
          height: 88px;
          margin: 8px auto;
        }

        .highlight {
          font-size: 48px;
        }
      }

      .description {
        text-align: center;
        margin-bottom: 8px;
      }

      ul {
        margin-bottom: 32px;
      }

      .desktop-only {
        display: block !important;
      }

      .mobile-only {
        display: none !important;
      }

      .limited-offer-background {
        top: 0;
        bottom: 0;
        width: 100%;
        object-fit: cover;
        object-position: top;
        height: 100%;
      }
    }

    &.fa {
      ::v-deep .limited-offer-holder {
        background-image: url('../assets/images/campaigns/fa-fw2024-bg-footer-mobile.webp');
      }
    }
  }
}
</style>
